import type { CSSProperties } from "react";

import { contrastRatio, parseHexColor, quizAccent, quizAccentStyle } from "@/features/quiz/lib/quiz-theme";

/**
 * `EX-SPN01`, `docs/29` §6.23: the contest page's accent colour, held to the
 * quiz's contrast guard (`QZ-BLD05`, `quiz-theme.ts`) and never a second
 * copy of it. The contest head is the Judge's near-black (`bg-neutral-950`),
 * the quiz player's ground within a shade, so the guard's two answers are the
 * two the head needs: `fill` for a button white words sit on (4.5:1), and
 * `onDark` for an icon or the accent line on the head (3:1).
 *
 * `null` without a colour: the page keeps the brand orange everywhere.
 */
export type ContestAccent = {
  /** What the organizer picked, as `#RRGGBB`. */
  color: string;
  fill: string;
  onDark: string;
  /** `fill` is darker than `color`: the console says so beside the swatches. */
  adjusted: boolean;
  /** White words on `color` itself, for the console's line. */
  ratio: number;
};

export function contestAccent(accentColor: string | null | undefined): ContestAccent | null {
  const color = parseHexColor(accentColor);
  if (!color) return null;
  const { fill, onDark, adjusted } = quizAccent({ primaryColor: color });
  return { color, fill, onDark, adjusted, ratio: contrastRatio(color, "#FFFFFF") };
}

/**
 * The quiz's own CSS variables for a subtree (`quizAccentStyle`):
 * `--theme-accent`, the fill `Button variant="accent"` paints, and
 * `--theme-accent-on-dark`. Nothing without a colour, so the subtree keeps
 * the brand orange the variant falls back to.
 */
export function contestAccentStyle(accentColor: string | null | undefined): CSSProperties | undefined {
  const color = parseHexColor(accentColor);
  return color ? quizAccentStyle({ primaryColor: color }) : undefined;
}

/** The six colours the console offers before a hex is typed (the canvas's swatches). */
export const CONTEST_ACCENT_SWATCHES = [
  { key: "orange", hex: "#E94D1D" },
  { key: "blue", hex: "#1D4ED8" },
  { key: "teal", hex: "#0F766E" },
  { key: "violet", hex: "#6D28D9" },
  { key: "forest", hex: "#166534" },
  { key: "ink", hex: "#111827" },
] as const;
